import _ from 'lodash';
import { AnyAction, ReducersMapObject } from 'redux';
import { FAILURE, NOT_MODIFIED, REQUEST, SUCCESS_DELETE, SUCCESS_FETCH } from 'src/store/entities/actions';
import { EntityDescriptions } from 'src/store/entities/types';
import { entityCollectionDefaultState, EntityCollectionState } from 'src/store/entities/types/EntityCollectionState';
import EntityDescription from 'src/store/entities/types/EntityDescription';

/**
 * Reducer Actions such as REQUEST, FAILURE, etc.
 */
interface ReducerActions {
    REQUEST: string;
    SUCCESS_FETCH: string;
    SUCCESS_DELETE: string;
    NOT_MODIFIED: string;
    FAILURE: string;
}

/**
 * Creates reducers for all entities defined in {@link EntityDescriptions}
 */
export default function entitiesReducers(): ReducersMapObject {
    const reducers: any = {};

    // create reducers for each entity description
    _.forEach(EntityDescriptions, (value: EntityDescription) => {
        reducers[value.reducer] = createReducer(value.name);
    });

    return reducers;
}

/**
 * This generic reducer function processes all actions dependent on action types
 */
export function processReducer(
    state: EntityCollectionState<any> = entityCollectionDefaultState(),
    action: AnyAction,
    reducerActions: ReducerActions
) {
    switch (action.type) {
        case reducerActions.REQUEST:
            return _.assign({}, state, { isFetching: true, error: undefined });
        case reducerActions.SUCCESS_FETCH: {
            const entities = getFetchedEntitiesFromResponse(action);
            let items: any[] = [];

            // receive collection of entities
            if (Array.isArray(entities)) {
                // merges the received items instead of replacing them
                if (action.mergeItems) {
                    items = state.items.slice(0, state.items.length);
                    entities.forEach((entity: any) => {
                        mergeItem(items, entity);
                    });

                    // replace existing items with new ones
                } else {
                    entities.forEach((entity: any) => {
                        items.push(entity);
                    });
                }

                // merge single received entity into existing entities collection
            } else {
                items = state.items.slice(0, state.items.length);
                mergeItem(items, entities);
            }

            return _.assign({}, state, { isFetching: false, items, error: undefined });
        }
        case reducerActions.NOT_MODIFIED: {
            return {
                error: undefined,
                isFetching: false,
                items: state.items,
            };
        }
        case reducerActions.SUCCESS_DELETE: {
            const items = state.items.slice(0, state.items.length);
            const existingIndex = items.findIndex((item: any) => item.id === action.response.data.id);

            if (existingIndex !== -1) items.splice(existingIndex, 1);

            return {
                error: undefined,
                isFetching: false,
                items,
            };
        }
        case reducerActions.FAILURE:
            return _.assign({}, state, { isFetching: false, error: action.error });
        default:
            return state;
    }
}

/**
 * Merges the entity into the given array
 */
const mergeItem = (items: any[], entity: any) => {
    const existingIndex = items.findIndex((item: any) => item.id === entity.id);

    if (existingIndex === -1) items.push(entity);
    else items[existingIndex] = entity;
};

/**
 * Extracts the entities form the response and takes care of the received format
 */
function getFetchedEntitiesFromResponse(action: AnyAction): any[] {
    const items = action.response.data;
    if (items != null) {
        if (items._embedded != null) return items._embedded[action.entityDescription.repository];
        else if (Array.isArray(items)) return items;
        else if (Array.isArray(items[action.entityDescription.repository]))
            return items[action.entityDescription.repository];
        else if (items.content != null && (items.content.id || Array.isArray(items.content))) return items.content;
        else return items;
    }

    return [];
}

const createReducer = (name: string) => {
    return (state: any = {}, action: AnyAction) => {
        const actions = {
            REQUEST: `${name}_${REQUEST}`,
            SUCCESS_FETCH: `${name}_${SUCCESS_FETCH}`,
            SUCCESS_DELETE: `${name}_${SUCCESS_DELETE}`,
            NOT_MODIFIED: `${name}_${NOT_MODIFIED}`,
            FAILURE: `${name}_${FAILURE}`,
        };

        switch (action.type) {
            case actions.REQUEST:
            case actions.SUCCESS_FETCH:
            case actions.SUCCESS_DELETE:
            case actions.NOT_MODIFIED:
            case actions.FAILURE:
                const projection: string = action.projection ? action.projection : 'default';

                return {
                    ...state,
                    [projection]: processReducer(state[projection], action, actions),
                };

            default:
                return state;
        }
    };
};
